import { MentionItem, MentionListRef } from "./BriefEditor.model";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
  useRef,
} from "react";
import styles from "./BriefMentionList.module.scss";

interface MentionListProps {
  items: MentionItem[];
  command: (item: { id: string; label: string }) => void;
}

export const BriefMentionList = forwardRef<MentionListRef, MentionListProps>(
  (props, ref) => {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const selectedItemRef = useRef<HTMLButtonElement>(null);

    const selectItem = (index: number) => {
      const item = props.items[index];
      if (item) {
        props.command({ id: item.id, label: item.name });
      }
    };

    const upHandler = () => {
      setSelectedIndex(
        (prev) => (prev + props.items.length - 1) % props.items.length
      );
    };

    const downHandler = () => {
      setSelectedIndex((prev) => (prev + 1) % props.items.length);
    };

    const enterHandler = () => {
      selectItem(selectedIndex);
    };

    useEffect(() => setSelectedIndex(0), [props.items]);

    useEffect(() => {
      if (selectedItemRef.current && containerRef.current) {
        const container = containerRef.current;
        const selected = selectedItemRef.current;

        const containerRect = container.getBoundingClientRect();
        const selectedRect = selected.getBoundingClientRect();

        if (selectedRect.top < containerRect.top) {
          selected.scrollIntoView({ block: "nearest", behavior: "smooth" });
        } else if (selectedRect.bottom > containerRect.bottom) {
          selected.scrollIntoView({ block: "nearest", behavior: "smooth" });
        }
      }
    }, [selectedIndex]);

    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }: { event: KeyboardEvent }) => {
        if (event.key === "ArrowUp") {
          upHandler();
          return true;
        }

        if (event.key === "ArrowDown") {
          downHandler();
          return true;
        }

        if (event.key === "Enter") {
          enterHandler();
          return true;
        }

        return false;
      },
    }));

    return (
      <div
        ref={containerRef}
        onWheel={(e) => {
          e.stopPropagation();
        }}
        className={styles.container}
      >
        {props.items.length === 0 ? (
          <div className={styles.noResults}>No results</div>
        ) : (
          props.items.map((item: MentionItem, index: number) => (
            <button
              key={item.id}
              ref={index === selectedIndex ? selectedItemRef : null}
              type="button"
              onClick={() => selectItem(index)}
              onMouseEnter={() => setSelectedIndex(index)}
              className={`${styles.item} ${
                index === selectedIndex ? styles.selected : ""
              }`}
            >
              <div className={styles.name}>{item.name}</div>
              <div className={styles.role}>{item.role}</div>
            </button>
          ))
        )}
      </div>
    );
  }
);

BriefMentionList.displayName = "BriefMentionList";
